import React, { useState, useEffect } from 'react';

const CONFERENCE_API = process.env.REACT_APP_CONFERENCE_API_URL || 'http://localhost:8080/conference';

const ReferralManagement = () => {
  const [user, setUser] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [approvalForm, setApprovalForm] = useState({
    approvedRole: 'REVIEWER',
    rejectionReason: ''
  });

  useEffect(() => {
    const userData = localStorage.getItem('adminUser');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchReferrals();
    }
  }, [user]);

  const fetchReferrals = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${CONFERENCE_API}/api/reviewer-referrals/pending`);
      if (response.ok) {
        const data = await response.json();
        setReferrals(data);
      }
    } catch (error) {
      console.error('Error fetching referrals:', error);
      showAlert('Failed to fetch referrals', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (message, type = 'success') => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 5000);
  };

  const handleOpenApprovalModal = (referral) => {
    setSelectedReferral(referral);
    setApprovalForm({
      approvedRole: referral.proposedRole || 'REVIEWER',
      rejectionReason: ''
    });
    setShowApprovalModal(true);
  };

  const handleCloseApprovalModal = () => {
    setShowApprovalModal(false);
    setSelectedReferral(null);
    setApprovalForm({
      approvedRole: 'REVIEWER',
      rejectionReason: ''
    });
  };

  const handleApprove = async () => {
    if (!selectedReferral) return;

    setLoading(true);
    try {
      const response = await fetch(`${CONFERENCE_API}/api/reviewer-referrals/${selectedReferral.id}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          approvedById: user.id,
          approvedRole: approvalForm.approvedRole
        })
      });

      if (response.ok) {
        showAlert('Referral approved successfully! Account created and email sent.', 'success');
        handleCloseApprovalModal();
        fetchReferrals();
      } else {
        const errorText = await response.text();
        showAlert(errorText || 'Failed to approve referral', 'error');
      }
    } catch (error) {
      console.error('Error approving referral:', error);
      showAlert('Failed to approve referral', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedReferral) return;

    setLoading(true);
    try {
      const response = await fetch(`${CONFERENCE_API}/api/reviewer-referrals/${selectedReferral.id}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          rejectedById: user.id,
          reason: approvalForm.rejectionReason
        })
      });

      if (response.ok) {
        showAlert('Referral rejected', 'success');
        handleCloseApprovalModal();
        fetchReferrals();
      } else {
        const errorText = await response.text();
        showAlert(errorText || 'Failed to reject referral', 'error');
      }
    } catch (error) {
      console.error('Error rejecting referral:', error);
      showAlert('Failed to reject referral', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: '20px' }}>
        <h2>Reviewer Referrals</h2>
        <p style={{ color: '#666', marginTop: '8px' }}>
          Review and approve/reject reviewer referrals from existing reviewers
        </p>
      </div>

      {alert && (
        <div style={{
          padding: '12px',
          marginBottom: '20px',
          borderRadius: '4px',
          backgroundColor: alert.type === 'success' ? '#d4edda' : '#f8d7da',
          color: alert.type === 'success' ? '#155724' : '#721c24',
          border: `1px solid ${alert.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          {alert.message}
        </div>
      )}

      {loading && !showApprovalModal ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Loading referrals...</p>
        </div>
      ) : referrals.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          color: '#666',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px'
        }}>
          <h3 style={{ marginTop: 0 }}>No Pending Referrals</h3>
          <p>There are currently no reviewer referrals waiting for approval.</p>
        </div>
      ) : (
        <div style={{ 
          backgroundColor: 'white', 
          borderRadius: '8px', 
          overflow: 'hidden',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Referee Name</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Email</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Referred By</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Track</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Proposed Role</th>
                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Date</th>
                <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {referrals.map(referral => (
                <tr key={referral.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '12px', fontWeight: '500' }}>{referral.refereeFullName}</td>
                  <td style={{ padding: '12px' }}>{referral.refereeEmail}</td>
                  <td style={{ padding: '12px' }}>{referral.referredByName}</td>
                  <td style={{ padding: '12px' }}>{referral.trackName || '-'}</td>
                  <td style={{ padding: '12px' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '12px',
                      backgroundColor: referral.proposedRole === 'CHAIR' ? '#007bff' : '#28a745',
                      color: 'white',
                      fontSize: '12px'
                    }}>
                      {referral.proposedRole}
                    </span>
                  </td>
                  <td style={{ padding: '12px' }}>{formatDate(referral.createdAt)}</td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <button
                      onClick={() => handleOpenApprovalModal(referral)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Review
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Approval Modal */}
      {showApprovalModal && selectedReferral && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '30px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}>
            <h2 style={{ margin: '0 0 20px 0', color: '#333' }}>Review Referral</h2>

            <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
              <div style={{ marginBottom: '10px' }}>
                <strong>Referee:</strong> {selectedReferral.refereeFullName}
              </div>
              <div style={{ marginBottom: '10px' }}>
                <strong>Email:</strong> {selectedReferral.refereeEmail}
              </div>
              {selectedReferral.refereePhone && (
                <div style={{ marginBottom: '10px' }}>
                  <strong>Phone:</strong> {selectedReferral.refereePhone}
                </div>
              )}
              <div style={{ marginBottom: '10px' }}>
                <strong>Referred By:</strong> {selectedReferral.referredByName}
              </div>
              {selectedReferral.trackName && (
                <div style={{ marginBottom: '10px' }}>
                  <strong>Track:</strong> {selectedReferral.trackName}
                </div>
              )}
              <div style={{ marginBottom: '10px' }}>
                <strong>Proposed Role:</strong> {selectedReferral.proposedRole}
              </div>
              {selectedReferral.comments && (
                <div>
                  <strong>Comments:</strong>
                  <p style={{ marginTop: '5px', color: '#666' }}>{selectedReferral.comments}</p>
                </div>
              )}
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>
                Approve as Role:
              </label>
              <select
                value={approvalForm.approvedRole}
                onChange={(e) => setApprovalForm({ ...approvalForm, approvedRole: e.target.value })}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value="REVIEWER">Reviewer</option>
                <option value="CHAIR">Chair</option>
                <option value="ADMIN">Admin</option>
              </select>
              <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                You can change the role from the proposed one if needed.
              </p>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: '#333' }}>
                Rejection Reason (if rejecting):
              </label>
              <textarea
                value={approvalForm.rejectionReason}
                onChange={(e) => setApprovalForm({ ...approvalForm, rejectionReason: e.target.value })}
                rows="3"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
                placeholder="Optional: Provide a reason for rejection"
              />
            </div>

            <div style={{ 
              display: 'flex', 
              gap: '10px', 
              justifyContent: 'flex-end',
              borderTop: '1px solid #f0f0f0',
              paddingTop: '20px'
            }}>
              <button
                type="button"
                onClick={handleCloseApprovalModal}
                disabled={loading}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleReject}
                disabled={loading}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  opacity: loading ? 0.6 : 1
                }}
              >
                {loading ? 'Processing...' : 'Reject'}
              </button>
              <button
                type="button"
                onClick={handleApprove}
                disabled={loading}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  opacity: loading ? 0.6 : 1
                }}
              >
                {loading ? 'Processing...' : 'Approve & Create Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralManagement;
